import React, { useEffect } from 'react';
import { Button, DatePicker, Drawer, Form, Input, Space, Switch } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import locale from 'antd/es/date-picker/locale/tr_TR';

const { TextArea } = Input;

export type RenewalSettingsFormValues = {
  renewal?: boolean;
  renewalDate?: Dayjs;
  note?: string;
};

type RenewalSettingsDrawerProps = {
  open: boolean;
  proposal?: any | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (values: RenewalSettingsFormValues) => Promise<void> | void;
};

const RenewalSettingsDrawer: React.FC<RenewalSettingsDrawerProps> = ({
  open,
  proposal,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm<RenewalSettingsFormValues>();
  const renewal = Form.useWatch('renewal', form);
  const todayStart = dayjs().startOf('day');

  const isPastDate = (date?: Dayjs | null) =>
    !!date && date.startOf('day').isBefore(todayStart);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    const rawRenewalDate = proposal?.renewalDate || proposal?.createdAt;
    form.setFieldsValue({
      renewal: proposal?.renewal ?? true,
      renewalDate: rawRenewalDate ? dayjs(rawRenewalDate) : undefined,
      note: '',
    });
  }, [form, open, proposal]);

  return (
    <Drawer
      title="Yenileme Ayarları"
      open={open}
      onClose={onClose}
      width={420}
      destroyOnClose
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>İptal</Button>
          <Button type="primary" loading={loading} onClick={() => form.submit()}>
            Kaydet
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item label="Teklif Yenileme Takibi Yapılacak mı?" name="renewal" valuePropName="checked">
          <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
        </Form.Item>
        <Form.Item
          label="Planlanan Teklif Hazırlama Tarihi"
          name="renewalDate"
          dependencies={['renewal']}
          rules={[
            ({ getFieldValue }) => ({
              required: Boolean(getFieldValue('renewal')),
              message: 'Yenileme tarihi seçiniz',
            }),
            ({ getFieldValue }) => ({
              validator: (_, value?: Dayjs) => {
                if (!getFieldValue('renewal') || !isPastDate(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Yenileme tarihi bugünden eski olamaz'));
              },
            }),
          ]}
        >
          <DatePicker
            style={{ width: '100%' }}
            locale={locale}
            format="DD.MM.YYYY"
            disabledDate={renewal ? isPastDate : undefined}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default RenewalSettingsDrawer;
